import { useId } from 'react';

import { VisuallyHidden } from '/imports/ui/components/VisuallyHidden';
import { isLight, styled, useTheme } from '/imports/ui/foundation/Theme';

const DEFAULT_SIZE = 32;

export interface ThemeToggleProps {
  size?: number;
  state?: 'default' | 'hover' | 'active';
}

export const ThemeToggle = ({ state = 'default', size = DEFAULT_SIZE }: ThemeToggleProps) => {
  const clipPathId = `theme-toggle-clip-path-${useId()}`;
  const { colorScheme, setColorScheme } = useTheme();
  const toggleColorScheme = (checked: boolean) => setColorScheme(checked ? 'dark' : 'light');

  return (
    <Container
      title="Toggle light and dark theme"
      className={`is-${state}`}
      style={{ width: size }}
    >
      <input
        type="checkbox"
        checked={!isLight(colorScheme)}
        onChange={(evt) => toggleColorScheme(evt.target.checked)}
      />
      <VisuallyHidden>Toggle theme</VisuallyHidden>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        height={size * 0.625}
        width={size * 0.625}
        viewBox="0 0 24 24"
      >
        <clipPath id={clipPathId}>
          <path d="M0 0h25a1 1 0 0010 10v14H0Z" />
        </clipPath>
        <g clipPath={`url(#${clipPathId})`}>
          <circle cx="12" cy="12" r="5" fill="currentColor" />
          <g
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="0"
            strokeWidth="2.5"
          >
            <path fill="none" d="M12 1.4v2.4" paintOrder="stroke markers fill" />
            <path fill="none" d="m20.3 3.7-2.5 2.5" paintOrder="stroke markers fill" />
            <path fill="none" d="M22.6 12h-2.4" paintOrder="stroke markers fill" />
            <path fill="none" d="M12 22.6v-2.4" paintOrder="stroke markers fill" />
            <path fill="none" d="M1.4 12h2.4" paintOrder="stroke markers fill" />
            <path fill="none" d="m20.3 20.3-2.5-2.5" paintOrder="stroke markers fill" />
            <path fill="none" d="m3.7 20.3 2.5-2.5" paintOrder="stroke markers fill" />
            <path fill="none" d="m3.7 3.7 2.5 2.5" paintOrder="stroke markers fill" />
          </g>
        </g>
      </svg>
    </Container>
  );
};

// -------------------------------------------------------------------------------------------------

const duration = '600ms';

/**
 * Based on https://toggles.dev/classic
 */
const Container = styled('label', ({ tokens }) => ({
  display: 'grid',
  placeContent: 'center',
  aspectRatio: '1 / 1',
  border: 'none',
  borderRadius: '50%',
  background: 'transparent',
  color: tokens.colorIcon,
  cursor: 'pointer',
  transitionProperty: 'background, color',
  transitionTimingFunction: tokens.motionEaseInOut,
  transitionDuration: tokens.motionDurationMid,
  transition: [
    `background ${tokens.motionDurationMid} ${tokens.motionEaseInOut}`,
    `color ${tokens.motionDurationFast} ${tokens.motionEaseInQuint}`,
  ].join(', '),

  '&.is-hover, &:hover': {
    background: tokens.colorBgTextHover,
    color: tokens.colorIconHover,
  },

  '&.is-active, &:active': {
    background: tokens.colorBgTextActive,
    color: tokens.colorAccent,
  },

  'input[type="checkbox"]': {
    display: 'none',
  },

  svg: {
    display: 'block',

    '*': {
      transformOrigin: 'center',
      transitionTimingFunction: 'cubic-bezier(0, 0, 0.05, 1.15)',
      transitionProperty: 'opacity, transform, d',
      transitionDuration: duration,
    },

    g: {
      path: {
        transitionDuration: `calc(${duration} * 0.8)`,
        transitionDelay: `calc(${duration} * 0.2)`,
      },
    },
  },

  'input[type="checkbox"]:checked ~ svg': {
    g: {
      path: {
        transform: 'scale(0.5) rotate(45deg)',
        opacity: 0,
        transitionDelay: '0s',
      },
    },
    circle: {
      transform: 'scale(1.7)',
    },
    clipPath: {
      path: {
        d: 'path("M0 2h13a1 1 0 0010 10v14H0Z")',
        transitionDelay: `calc(${duration} * 0.2)`,
      },
    },

    '@supports not (d: path(""))': {
      clipPath: {
        path: {
          transform: 'translate3d(-12px, 2px, 0)',
        },
      },
    },
  },
}));
